import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactCrop, { Crop, PixelCrop, makeAspectCrop, centerCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button, Modal } from 'react-bootstrap';

interface ImageCropProps {
  show: boolean;
  onClose: () => void;
  onCropComplete: (croppedImageBlob: Blob) => void;
  aspect?: number;
  initialFile?: File | null;
}

const ImageCrop: React.FC<ImageCropProps> = ({ show, onClose, onCropComplete, aspect = 1, initialFile = null }) => {
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Load initial file if provided
  useEffect(() => {
    if (initialFile && show) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImgSrc(reader.result?.toString() || '');
        setScale(1);
        setRotate(0);
      });
      reader.readAsDataURL(initialFile);
    } else if (!show) {
      setImgSrc('');
      setCrop(undefined);
      setCompletedCrop(undefined);
      setScale(1);
      setRotate(0);
    }
  }, [initialFile, show]);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImgSrc(reader.result?.toString() || '');
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const crop = makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      width,
      height
    );
    setCrop(centerCrop(crop, width, height));
  }, [aspect]);

  const getCroppedImg = useCallback(async () => {
    if (!imgRef.current || !crop) {
      console.error('Image or crop not available');
      return;
    }

    const image = imgRef.current;
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error('Canvas not available');
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('No 2d context');
    }

    // Use completedCrop if available (it's already in natural image coordinates)
    // Otherwise, convert crop to natural image coordinates
    let pixelCrop: PixelCrop;
    
    if (completedCrop) {
      // completedCrop is already in natural image coordinates
      pixelCrop = completedCrop;
    } else {
      // Convert crop to natural image coordinates
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      
      if (crop.unit === '%') {
        pixelCrop = {
          x: (crop.x / 100) * image.naturalWidth,
          y: (crop.y / 100) * image.naturalHeight,
          width: (crop.width / 100) * image.naturalWidth,
          height: (crop.height / 100) * image.naturalHeight,
          unit: 'px',
        };
      } else {
        pixelCrop = {
          x: crop.x * scaleX,
          y: crop.y * scaleY,
          width: crop.width * scaleX,
          height: crop.height * scaleY,
          unit: 'px',
        };
      }
    }
    
    // Ensure crop coordinates are within image bounds
    const clampedCrop = {
      x: Math.max(0, Math.min(pixelCrop.x, image.naturalWidth)),
      y: Math.max(0, Math.min(pixelCrop.y, image.naturalHeight)),
      width: Math.max(1, Math.min(pixelCrop.width, image.naturalWidth - Math.max(0, pixelCrop.x))),
      height: Math.max(1, Math.min(pixelCrop.height, image.naturalHeight - Math.max(0, pixelCrop.y))),
    };

    // Output size is the cropped area size
    const outputWidth = Math.round(clampedCrop.width);
    const outputHeight = Math.round(clampedCrop.height);
    
    const pixelRatio = window.devicePixelRatio;
    canvas.width = outputWidth * pixelRatio;
    canvas.height = outputHeight * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    // Calculate the source rectangle in natural image coordinates
    const sourceX = Math.round(clampedCrop.x);
    const sourceY = Math.round(clampedCrop.y);
    const sourceWidth = Math.round(clampedCrop.width);
    const sourceHeight = Math.round(clampedCrop.height);

    // Draw the cropped portion directly
    ctx.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      outputWidth,
      outputHeight
    );

    canvas.toBlob((blob) => {
      if (blob) {
        onCropComplete(blob);
        onClose();
        setImgSrc('');
        setCrop(undefined);
        setCompletedCrop(undefined);
      } else {
        console.error('Failed to create blob');
      }
    }, 'image/jpeg', 0.9);
  }, [completedCrop, crop, onCropComplete, onClose]);

  const handleClose = () => {
    setImgSrc('');
    setCrop(undefined);
    setCompletedCrop(undefined);
    onClose();
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      centered 
      size="lg" 
      className="modal-datta"
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
          انتخاب و کراپ عکس
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!imgSrc ? (
          <div>
            <input
              type="file"
              accept="image/*"
              onChange={onSelectFile}
              style={{ fontFamily: 'Vazir, sans-serif' }}
            />
            <p style={{ fontFamily: 'Vazir, sans-serif', color: 'var(--text-secondary)', marginTop: '10px', fontSize: '14px' }}>
              لطفاً یک فایل تصویری انتخاب کنید
            </p>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontFamily: 'Vazir, sans-serif', marginLeft: '10px' }}>
                بزرگنمایی:
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={scale}
                  onChange={(e) => setScale(Number(e.target.value))}
                  style={{ margin: '0 10px' }}
                />
                {scale.toFixed(1)}x
              </label>
              <label style={{ fontFamily: 'Vazir, sans-serif', marginLeft: '20px' }}>
                چرخش:
                <input
                  type="range"
                  min="0"
                  max="360"
                  step="1"
                  value={rotate}
                  onChange={(e) => setRotate(Number(e.target.value))}
                  style={{ margin: '0 10px' }}
                />
                {rotate}°
              </label>
            </div>
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => {
                setCrop(percentCrop);
                // Also update completedCrop when crop changes
                if (imgRef.current) {
                  const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
                  const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
                  if (percentCrop.unit === '%') {
                    setCompletedCrop({
                      x: (percentCrop.x / 100) * imgRef.current.naturalWidth,
                      y: (percentCrop.y / 100) * imgRef.current.naturalHeight,
                      width: (percentCrop.width / 100) * imgRef.current.naturalWidth,
                      height: (percentCrop.height / 100) * imgRef.current.naturalHeight,
                      unit: 'px',
                    });
                  } else {
                    setCompletedCrop({
                      x: percentCrop.x * scaleX,
                      y: percentCrop.y * scaleY,
                      width: percentCrop.width * scaleX,
                      height: percentCrop.height * scaleY,
                      unit: 'px',
                    });
                  }
                }
              }}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
              minWidth={100}
            >
              <img
                ref={imgRef}
                alt="Crop me"
                src={imgSrc}
                style={{ transform: `scale(${scale}) rotate(${rotate}deg)`, maxWidth: '100%' }}
                onLoad={onImageLoad}
              />
            </ReactCrop>
            <canvas
              ref={canvasRef}
              style={{ display: 'none' }}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} style={{ fontFamily: 'Vazir, sans-serif' }}>
          انصراف
        </Button>
        {imgSrc && (
          <Button variant="primary" onClick={getCroppedImg} style={{ fontFamily: 'Vazir, sans-serif' }}>
            تایید و ذخیره
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ImageCrop;

